import React, { useState } from 'react'
import Pagination from 'components/molecules/Pagination/Pagination'
import { deletePattern } from 'services/templateService'
import { LoaderTransparent } from 'components/molecules/LoaderTransparent/LoaderTransparent'
import PatternCardItem from 'templates/presentation/components/molecules/PatternCardItem/PatternCardItem'
import { Box } from 'components/atoms/AppBox'
import { useNavigate } from 'react-router-dom'
import StatusToast from 'components/molecules/StatusToast/StatusToast'
import { useTemplates } from 'templates/presentation/hooks/useTemplates'
import { TemplateFilterBar } from 'templates/presentation/components/organisms/TemplateFilterBar'
import { TemplateFilters } from 'templates/domain/entities/Template'
import { TemplateStatus } from 'templates/domain/entities/TemplateStatus'
import { formatDateToApiParam } from 'utils/formatting'

interface RunFetchOverrides {
    tab?: string
    status?: TemplateStatus
    name?: string
    dateRange?: { start: Date | null; end: Date | null }
    page?: number
}

export default function PatternList() {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState<string>('all')
    const [activeStatus, setActiveStatus] = useState<TemplateStatus | undefined>(undefined)
    const [searchName, setSearchName] = useState<string>('')
    const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: null,
        end: null,
    })
    const [messageError, setMessageError] = useState('')
    const [toastType, setToastType] = useState<'error' | 'success' | 'loading'>('error')

    const { templates, loading, pagination, fetchTemplates } = useTemplates()

    const runFetch = (overrides: RunFetchOverrides = {}) => {
        const tab = overrides.tab ?? activeTab
        const status = 'status' in overrides ? overrides.status : activeStatus
        const name = overrides.name ?? searchName
        const range = overrides.dateRange ?? dateRange
        const page = overrides.page ?? pagination.page

        const filters: TemplateFilters = { page }
        if (tab !== 'all') filters.operationModeId = parseInt(tab, 10)
        if (status) filters.status = status
        if (name) filters.name = name
        if (range.start && range.end) {
            filters.created_after = formatDateToApiParam(range.start)
            filters.created_before = formatDateToApiParam(range.end)
        }

        fetchTemplates(filters)
    }

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId)
        runFetch({ tab: tabId, page: 1 })
    }

    const handleStatusChange = (status?: TemplateStatus) => {
        setActiveStatus(status)
        runFetch({ status, page: 1 })
    }

    const handleSearchChange = (name: string) => {
        setSearchName(name)
        runFetch({ name, page: 1 })
    }

    const handleDateChange = (range: { start: Date | null; end: Date | null }) => {
        setDateRange(range)
        if (range.start && range.end) {
            runFetch({ dateRange: range, page: 1 })
        }
    }

    const changePage = (page: number) => {
        runFetch({ page })
    }

    const deleteTemplate = async (id: number) => {
        try {
            await deletePattern(id)
            setToastType('success')
            setMessageError('Plantilla eliminada satisfactoriamente')
            runFetch()
        } catch (error) {
            setToastType('error')
            setMessageError(
                typeof error === 'object' && error !== null && 'message' in error
                    ? String((error as { message: unknown }).message)
                    : 'Error al eliminar la plantilla'
            )
        }
        setTimeout(() => setMessageError(''), 3000)
    }

    return (
        <div>
            {messageError && <StatusToast title={messageError} type={toastType} />}
            <Box>
                {loading && <LoaderTransparent />}

                <TemplateFilterBar
                    activeTab={activeTab}
                    activeStatus={activeStatus}
                    totalAllTemplates={pagination.totalAllTemplates}
                    totalByClassificationType={pagination.totalByClassificationType}
                    onTabChange={handleTabChange}
                    onStatusChange={handleStatusChange}
                    onSearchChange={handleSearchChange}
                    onDateChange={handleDateChange}
                />

                <div className="flex flex-col justify-between h-full">
                    <div className="overflow-y-auto" style={{ height: '500px' }}>
                        {templates.length > 0 ? (
                            templates.map((template, index) => (
                                <PatternCardItem
                                    key={index}
                                    id={template.id!}
                                    title={template.name}
                                    countRules={template.countGroups || 0}
                                    countChutes={template.countChutes || 0}
                                    operationMode={template.operationMode}
                                    status={template.status}
                                    statusName={template.statusName}
                                    nameWarehouse={template.warehouseName || ''}
                                    onSubmit={() => navigate(`/templates/show/${template.id}`)}
                                    deletePattern={() => template.id && deleteTemplate(template.id)}
                                    editPattern={() =>
                                        navigate(`/templates/edit/${template.id}`, {
                                            state: { mode: 'edit' },
                                        })
                                    }
                                    duplicatePattern={() =>
                                        navigate('/templates/create', {
                                            state: { duplicateFrom: template.id },
                                        })
                                    }
                                />
                            ))
                        ) : (
                            <div className="flex justify-center p-4 text-sm text-[#475569]">
                                No hay plantillas para mostrar
                            </div>
                        )}
                    </div>
                    <div className="flex justify-center mt-10">
                        <Pagination
                            totalPages={Math.ceil(pagination.totalItems / pagination.itemsPerPage)}
                            currentPage={pagination.page}
                            onPageChange={changePage}
                        />
                    </div>
                </div>
            </Box>
        </div>
    )
}
